/**
 * Theme Route Initializer
 * Automatically imports and registers theme routes based on active theme configuration
 * Keeps core pages clean and theme-agnostic
 */
import { logger } from '@/lib/logger.client';
import { debugRouteRegistry } from '@/lib/route-loader';

/**
 * Registry to track which themes have been initialized
 * Prevents duplicate imports
 */
const initializedThemes = new Set<string>();

/**
 * Dynamically import and initialize theme routes based on theme name
 * This function is called automatically when the theme configuration is loaded
 */
export async function initializeThemeRoutes(themeName: string): Promise<void> {
  // Skip if already initialized
  if (initializedThemes.has(themeName)) {
    logger.debug({ msg: 'Theme routes already initialized', theme: themeName });
    return;
  }

  try {
    logger.debug({ msg: 'Initializing theme routes', theme: themeName });

    // Dynamically import the theme's route registry.
    // - On the server we import the full `routes/index.ts` (may contain server components).
    // - In the browser we MUST NOT import the full routes file because it can pull
    //   server-only modules (e.g. `config.server.ts`). Try to load a `routes.client`
    //   module instead and silently skip if it doesn't exist.
    if (typeof window === 'undefined') {
      // Server: load the full route registry
      await import(`@/themes/${themeName}/routes`);

      // Mark as initialized
      initializedThemes.add(themeName);

      logger.debug({ msg: 'Theme routes initialized (server)', theme: themeName });

      // Debug: log the registry state
      debugRouteRegistry();
    } else {
      // Client: only import client-safe route registry (routes.client.ts)
      try {
        await import(`@/themes/${themeName}/routes.client`);
        initializedThemes.add(themeName);
        logger.debug({ msg: 'Theme client routes initialized (client)', theme: themeName });
        debugRouteRegistry();
      } catch (err) {
        // No client-side route registry — this is expected for most themes.
        logger.debug({ msg: 'No client-side theme routes found; skipping client registration', theme: themeName });
      }
    }
  } catch (error) {
    // Theme might not have a routes registry - this is okay
    logger.debug({
      msg: 'Theme does not have route overrides or failed to load',
      theme: themeName,
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

/**
 * Initialize routes for all available themes
 * Useful for preloading all theme routes at app startup
 */
export async function initializeAllThemeRoutes(themeNames: string[]): Promise<void> {
  logger.debug({ msg: 'Initializing routes for all themes', themes: themeNames });

  await Promise.all(themeNames.map((themeName) => initializeThemeRoutes(themeName)));

  logger.debug({ msg: 'All theme routes initialized', themes: Array.from(initializedThemes) });
}

/**
 * Clear initialization cache
 * Useful for theme hot-reloading in development
 */
export function clearThemeRouteCache(): void {
  initializedThemes.clear();
  logger.debug({ msg: 'Theme route cache cleared' });
}
